// export default HeresAmerica;
import React, { useState, useEffect } from "react";
import { useN01SwitchStyles } from "@mui-treasury/styles/switch/n01";
import {
  Button,
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import HeresAmericaService from "../../services/HA/HeresAmericaService.js";
import SeriesDropdown from "./SeriesDropdown";
import { displaySuccessToast, displayErrorToast } from "../../utils/toast.js";
import MultipleDropDownHa from "./MultipleDropDownHa.jsx";
import CustomYesNoModal from "../micro_components/CustomYesNoModal.jsx";

function HeresAmerica({
  book,
  description,
  haBookLink,
  externalBookId,
  needExternalBook,
}) {
  const [haBookData, setHaBookData] = useState({});
  const [formData, setFormData] = useState({});
  const [isHaActive, setIsHaActive] = useState(false);
  const [ageGroups, setAgeGroups] = useState([]);
  const [allSeries, setAllSeries] = useState([]);
  const [ageGroup, setAgeGroup] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isNewBook, setIsNewBook] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    setFormData({ ...formData, coverFileName: haBookLink });
  }, [haBookLink]);

  useEffect(() => {
    if (book && book.id) {
      const bookId = book.id;
      getHABook(bookId);
    }
  }, [book]);

  useEffect(() => {
    fetchAllTags();
    fetchAllSeries();
    fetchAgeGroups();
  }, []);

  useEffect(() => {
    setFormData({ ...haBookData, description });

    setIsHaActive(haBookData.active);
  }, [haBookData]);

  useEffect(() => {
    setFormData({
      ...formData,
      description: description,
    });
  }, [description]);

  useEffect(() => {
    if (isNewBook && externalBookId) {
      if (haBookLink) {
        // const formattedFileLink = `https://d24hdgytax3x03.cloudfront.net/${book.id}/${book.coverFileName}`
        setFormData({
          ...formData,
          externalBookId: externalBookId,
          coverFileName: haBookLink,
          title: book?.title,
        });
      } else {
        setFormData({ ...formData, externalBookId: externalBookId });
      }
    }
  }, [isNewBook, book, haBookLink]);

  async function getHABook(bookId) {
    try {
      await fetchAllSeries();
      await fetchAgeGroups();
      const res = await HeresAmericaService.getBook(bookId);
      setHaBookData(res);
      if (res.ageGroup) {
        setAgeGroup(res.ageGroup.name);
      }
      if (res.tags) {
        // const tags = await formattedTags(res.tags, alltags)
        const tags = await res.tags.map((tag) => tag.id);

        setSelectedTags(tags);
      }
      console.log(res);
      setIsNewBook(false);
    } catch (error) {
      setIsNewBook(true);

      console.log(
        "🚀 ~ file: HeresAmericaComponent.jsx:77 ~ getHABook ~ error:",
        error.response.data
      );
    }
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    setFormData({ ...formData, active: isHaActive });
  }, [isHaActive]);

  useEffect(() => {
    setFormData({ ...formData, title: book.title });
  }, [book]);

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      // todo make api call to update
      const res = await HeresAmericaService.updateBook(formData, haBookData.id);
      displaySuccessToast(`Book Updated \n ${res.title}  `);
      if (res.active) {
        await revalidateHeresAmericaProjectPage(res.series.slug, res.slug);
      }
      setIsLoading(false);
      await submitBookImageDataToHA(res.externalBookId);

      // Update local state if necessary, or refresh data
    } catch (error) {
      displayErrorToast(`An error occurred. ${error}`);
      setIsLoading(false);

      console.error("Error updating book:", error);
    }
  };

  async function fetchAgeGroups() {
    try {
      const data = await HeresAmericaService.getAllAgeGroups();
      setAgeGroups(data);
    } catch (error) {
      console.error("Error fetching age groups:", error);
    }
  }

  async function fetchAllSeries() {
    try {
      let allSeriesData = [];
      let currentPage = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await HeresAmericaService.getAllSeries({
          pageSize: 100,
          page: currentPage,
        });
        const { series, pagination } = response;

        allSeriesData = [...allSeriesData, ...series];
        hasNextPage = pagination.hasNextPage;
        currentPage = pagination.currentPage + 1;
      }

      setAllSeries(allSeriesData);
    } catch (error) {
      console.error("Error fetching series:", error);
    }
  }

  async function fetchAllTags() {
    try {
      const data = await HeresAmericaService.getAllTags();

      setAllTags(data);

      return data;
    } catch (error) {
      console.error("Error fetching age groups:", error);
    }
  }

  const handleSeriesChange = (value) => {
    setFormData({
      ...formData,
      series: value,
      seriesId: value.id,
    });
  };

  const handleDropdownChange = (event) => {
    const { value } = event.target;

    setAgeGroup(value?.name);
    setFormData({
      ...formData,
      ageGroup: value,
      ageGroupId: value.id,
    });
  };

  const handleTagDropdownChange = (e) => {
    const { value } = e.target; //  value is an array of selected tag IDs
    setSelectedTags(value); // Update the state to hold selected tags
    setFormData({
      ...formData,
      tags: value, // Update the formData to hold the selected tags
    });
  };

  const deleteBook = async () => {
    setIsLoading(true);

    try {
      const res = await HeresAmericaService.deleteBook(formData.externalBookId);
      console.log(res);
      if (res) {
        await revalidateHeresAmericaProjectPage(res.series.slug, res.slug);
      }
      displaySuccessToast(`🗑️ Book Deleted 🗑️ `);
      setFormData({});
      setSelectedTags([]);

      setIsLoading(false);
      setIsNewBook(true);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      displayErrorToast(`An error occurred. ${error.response.data.error}`);
      // console.error('Error deleting book:', error.response.data.error);
    }
  };

  const revalidateHeresAmericaProjectPage = async (seriesSlug, bookSlug) => {
    console.log(
      "🚀 ~ revalidateHeresAmericaProjectPage ~ seriesSlug:",
      seriesSlug,
      "bookSlug:",
      bookSlug
    );
    try {
      const res = await HeresAmericaService.revalidateProjectBooks(
        seriesSlug,
        bookSlug
      );
      if (res) {
        displaySuccessToast("🎉  Book slug revalidated successfully. 🎉");
      }
    } catch (e) {
      console.log(e);
      displayErrorToast("Book  Revalidation FAILED.");
    }
  };

  const handleCreateBook = async () => {
    try {
      // Make the API call
      setIsLoading(true);
      console.log(formData);
      if (externalBookId) {
        formData.externalBookId = externalBookId;
      }
      const res = await HeresAmericaService.createBook(formData);

      if (res.series?.slug) {
        await revalidateHeresAmericaProjectPage(res.series.slug, res.slug);
      }
      // The book was successfully created
      setIsNewBook(false); // Update the state
      console.log("Book created successfully.");
      displaySuccessToast("🎉 Book created successfully. 🎉");

      setIsLoading(false);
    } catch (error) {
      // Handle API request errors here
      console.error("Error creating book:", error);

      displayErrorToast("Book FAILED successfully. ");
      setIsLoading(false);
    }
  };

  async function submitBookImageDataToHA(id) {
    try {
      const bookData = await getBookInfoForHAPageImgs(id);

      const res = await HeresAmericaService.submitBookImageDataToHA(bookData);
      console.log(res);
    } catch (e) {
      console.log(e);
    }
  }

  async function getBookInfoForHAPageImgs(id) {
    const href = `https://dpi-authoring-image.s3.us-west-1.amazonaws.com/${id}/bookDataVariation`;
    const response = await fetch(href);

    if (!response.ok) {
      throw new Error(`Failed to fetch book data for ID ${id}`);
    }
    const data = await response.json();
    console.log({ data });

    let pagesWithImageLink = [];
    data.chapters.map((c) => {
      c.pages.map((p) => {
        // Look for English variation
        const enVariation = p?.variations?.find((v) => v.variation === "en");
        if (enVariation?.pageImageLink) {
          pagesWithImageLink.push(enVariation.pageImageLink);
        }
      });
    });

    console.log("pagesWithImageLink: ", pagesWithImageLink);

    const firstPageUrl = pagesWithImageLink[0];
    const secondPageUrl = pagesWithImageLink[1];
    const thirdPageUrl = pagesWithImageLink[2];
    const fourthPageUrl = pagesWithImageLink[3];

    const pages = data.chapters.reduce((acc, chapter) => {
      return acc + (chapter?.pages?.length || 0);
    }, 0);

    return {
      externalBookId: id,
      totalPages: pages,
      firstPageUrl,
      secondPageUrl,
      fourthPageUrl,
      thirdPageUrl,
    };
  }

  useEffect(() => {
    console.log("FORM DATA");
    console.log(formData);
  }, [formData]);

  function openDeleteModal() {
    setDeleteModalOpen(true);
  }

  function closeDeleteModal() {
    setDeleteModalOpen(false);
  }

  if (book?.domain_id?.toString() === "14" && needExternalBook) {
    return (
      <h3 style={{ color: "red", fontWeight: "bold" }}>
        Please add a book first. This creates an external ID needed for
        uploading a "Here's America" book.
      </h3>
    );
  } else if (book?.domain_id?.toString() === "14") {
    return (
      <div className="heres-america-component">
        <h3>Here's America:</h3>
        {/* <Switch defaultChecked color="default" /> */}
        <div className="heres-america-component-switch">
          <OnOffSwitch
            handleClick={() => {
              setIsHaActive(!isHaActive);
            }}
            value={isHaActive}
          />
        </div>
        <TextField
          fullWidth
          id="filled-read-only-input"
          disabled
          InputProps={{
            readOnly: true,
          }}
          label="Here's America Book Id"
          variant="standard"
          name="id"
          value={formData.id || "Upload to Heres America to Recieve an Id"}
        />
        <TextField
          label="Title"
          variant="standard"
          name="title"
          fullWidth
          value={formData.title || ""}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          label="Cover File Name"
          variant="standard"
          name="coverFileName"
          value={formData.coverFileName || ""}
          onChange={handleInputChange}
        />

        <FormControl fullWidth variant="standard">
          <InputLabel id="age-group-label">
            {" "}
            {formData.ageGroup
              ? formData.ageGroup.name
              : `Select an Age Group  `}
          </InputLabel>
          <Select
            labelId="age-group-label"
            id="ageGroup"
            // name="ageGroupId"  // API expects the key to be ageGroupId
            value={ageGroup.name || ""}
            onChange={handleDropdownChange}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
          >
            <MenuItem value={ageGroup || ""}>
              {/* {formData.ageGroup ? formData.ageGroup.name : `Select an Age Group  `} */}
            </MenuItem>
            {ageGroups.map((group) => {
              if (group.name === formData.ageGroup?.name) {
                return null;
              }
              return (
                <MenuItem
                  key={group.id}
                  value={group}
                  style={{ padding: "8px 16px" }}
                >
                  {group.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <SeriesDropdown
          seriesList={allSeries}
          currentSeries={formData.series || ""}
          handleSeriesChange={handleSeriesChange}
        />
        <MultipleDropDownHa
          data={allTags}
          setValue={handleTagDropdownChange}
          value={selectedTags || ""}
        />

        {isNewBook ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateBook}
            disabled={isLoading}
          >
            Create Book
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdate}
              disabled={isLoading}
            >
              Update Book
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                revalidateHeresAmericaProjectPage(
                  haBookData.series.slug,
                  haBookData.slug
                );
              }}
              disabled={isLoading}
            >
              Refresh Heres America series page cache
            </Button>
          </>
        )}

        {!isNewBook && (
          <Button
            variant="contained"
            color="secondary"
            onClick={openDeleteModal}
            disabled={isLoading}
          >
            Delete Book
          </Button>
        )}
        <CustomYesNoModal
          title={"Delete this book from Here's America?"}
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          onSubmit={deleteBook}
          initial={""}
        />
      </div>
    );
  } else {
    return null;
  }
}

export default HeresAmerica;

const OnOffSwitch = ({ value, handleClick }) => {
  const switchStyles = useN01SwitchStyles();
  const [toggled, setToggled] = useState(value !== undefined ? value : false);

  const handleChange = () => {
    handleClick();
  };

  useEffect(() => {
    if (value !== undefined) {
      setToggled(value);
    }
  }, [value]);

  return (
    <div style={{ margin: "0px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
          height: "100%",
        }}
      >
        <Switch
          color="default"
          classes={switchStyles}
          checked={toggled}
          onClick={handleChange}
        />
      </div>
    </div>
  );
};
